import { useState, type CSSProperties } from 'react';
import { ConfirmDialog, EmptyState, ErrorState, Loading, StatusPill, useNow, useTitle } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { STATE_ART } from '../../lib/voice.js';
import { count } from '../../lib/format.js';
import { AdEditor } from './AdEditor';
import { NotReadyPanel } from './Checklist';
import { PLATES, SLOTS, STATUS_VIEW, ctr, scheduleView, slotsNow, statusActions } from './lib.js';
import { useAdStatus, useOpsAds } from './queries';
import type { AdStatus, OpsAd } from './types';
import './ads.css';

/** Ops · Ads: the Home top-slot ads (docs/HOME-ADS-CONTRACT.md §3). */
export function OpsAdsPage() {
  useTitle('Ads');
  // null = the list; 'new' = a new ad; otherwise the id of the ad being edited.
  const [editing, setEditing] = useState<string | null>(null);
  const ads = useOpsAds();
  const list = ads.data ?? [];

  if (editing !== null) {
    const ad = editing === 'new' ? null : (list.find((a) => a.id === editing) ?? null);
    if (editing !== 'new' && !ad) {
      return ads.isError ? <ErrorState error={ads.error} onRetry={() => void ads.refetch()} /> : <Loading label="Loading the ad" rows={3} />;
    }
    return <AdEditor ad={ad} onSaved={(a) => setEditing(a.id)} onClose={() => setEditing(null)} />;
  }
  return <AdsList query={ads} onNew={() => setEditing('new')} onEdit={(a) => setEditing(a.id)} />;
}

function AdsList({ query, onNew, onEdit }: { query: ReturnType<typeof useOpsAds>; onNew(): void; onEdit(ad: OpsAd): void }) {
  const now = useNow(30_000);
  const status = useAdStatus();
  const [archiving, setArchiving] = useState<OpsAd | null>(null);
  const list = query.data ?? [];
  const slots = slotsNow(list, now);
  const live = list.filter((a) => a.status === 'live').length;

  const change = (ad: OpsAd, to: AdStatus) => {
    if (to === 'archived') setArchiving(ad);
    else status.mutate({ ad, to });
  };

  return (
    <>
      <div className="top">
        <div className="grow stack s4">
          <h1 className="t-title">Ads</h1>
          <span className="t-monocap muted">
            HOME TOP SLOT · {live} LIVE · {slots.size}/{SLOTS} SLOTS FILLED NOW · SLOT 1 IS THE PREMIUM SLOT
          </span>
        </div>
        <button type="button" className="btn btn-primary" onClick={onNew}>
          <Icon name="plus" />
          New ad
        </button>
      </div>

      {status.notReady ? (
        <NotReadyPanel
          headline={status.notReady.ad.headline}
          missing={status.notReady.missing}
          onEdit={() => {
            const ad = status.notReady?.ad;
            status.clearNotReady();
            if (ad) onEdit(ad);
          }}
          onClose={status.clearNotReady}
        />
      ) : null}

      <section className="card panel" aria-labelledby="ad-list-h" aria-busy={query.isFetching || undefined}>
        <div className="stack s4">
          <h2 id="ad-list-h">All ads</h2>
          <p className="t-caption muted">
            Up to {SLOTS} live ads rotate at the top of Home, highest priority first. CTR is taps ÷ viewable views. Auto-rotated views (the banner moved on its own) are counted separately, as
            the MRC asks. Orders: by the tapping customer at the advertiser's shop within 24 h.
          </p>
        </div>
        {query.isError && !query.data ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : !query.data ? (
          <Loading label="Loading ads" rows={3} />
        ) : list.length === 0 ? (
          <EmptyState
            art={STATE_ART.noResults}
            title="No ads yet"
            action={
              <button type="button" className="btn btn-primary" onClick={onNew}>
                <Icon name="plus" />
                New ad
              </button>
            }
          >
            Until there's one, Home shows the PopCart house ad.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="ad-stats">
              <thead>
                <tr>
                  <th scope="col">Ad</th>
                  <th scope="col">Status</th>
                  <th scope="col">Schedule</th>
                  <th scope="col" className="num">
                    Priority
                  </th>
                  <th scope="col" className="num">
                    Served
                  </th>
                  <th scope="col" className="num">
                    Viewable
                  </th>
                  <th scope="col" className="num">
                    Auto-rotated
                  </th>
                  <th scope="col" className="num">
                    Taps
                  </th>
                  <th scope="col" className="num">
                    CTR
                  </th>
                  <th scope="col" className="num">
                    Orders
                  </th>
                  <th scope="col" className="num">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => {
                  const sv = STATUS_VIEW[a.status];
                  const sch = scheduleView(a, now);
                  const slot = slots.get(a.id);
                  const s = a.stats ?? { served: 0, viewable: 0, taps: 0, autoRotatedViewable: 0, ordersAttributed: 0 };
                  return (
                    <tr key={a.id}>
                      <th scope="row" style={{ textTransform: 'none', letterSpacing: 0, font: 'var(--pc-type-ui-label)', color: 'var(--pc-text-primary)' }}>
                        <span className="row" style={{ alignItems: 'flex-start' }}>
                          {a.imageUrl ? (
                            <img className="ad-thumb" src={a.imageUrl} alt="" />
                          ) : (
                            <span className="ad-thumb" style={{ '--ad-plate': `var(${(PLATES[a.plate] ?? PLATES.volt).token})` } as CSSProperties} aria-hidden="true" />
                          )}
                          <span className="ad-row-head">
                            <b>{a.headline}</b>
                            <span className="t-caption muted">{a.advertiserName}</span>
                            <span className="t-monocap muted">
                              {a.kind === 'video' ? 'VIDEO' : 'PHOTO'}
                              {a.dealLabel ? ` · ${a.dealLabel}` : ''}
                            </span>
                          </span>
                        </span>
                      </th>
                      <td>
                        <span className="stack s4" style={{ alignItems: 'flex-start' }}>
                          <StatusPill tone={sv.tone}>{sv.label}</StatusPill>
                          {slot ? <StatusPill tone="info">{`Slot ${slot}`}</StatusPill> : null}
                          {a.status !== 'archived' && a.missing?.length ? <span className="t-caption muted">{a.missing.length === 1 ? '1 thing to fix' : `${a.missing.length} things to fix`}</span> : null}
                        </span>
                      </td>
                      <td>
                        <span className="stack s4">
                          <span className="t-mono">{sch.range}</span>
                          <span className="t-monocap muted">{sch.label}</span>
                        </span>
                      </td>
                      <td className="num">{a.priority}</td>
                      <td className="num">{count(s.served)}</td>
                      <td className="num">{count(s.viewable)}</td>
                      <td className="num">{count(s.autoRotatedViewable)}</td>
                      <td className="num">{count(s.taps)}</td>
                      <td className="num">{ctr(s)}</td>
                      <td className="num">{count(s.ordersAttributed)}</td>
                      <td className="num">
                        <div className="actions row wrap" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-quiet" aria-label={`Edit ${a.headline}`} onClick={() => onEdit(a)}>
                            Edit
                          </button>
                          {statusActions(a.status).map((x) => (
                            <button
                              key={x.to}
                              type="button"
                              className={x.to === 'live' ? 'btn btn-primary' : 'btn'}
                              aria-label={`${x.label}: ${a.headline}`}
                              disabled={status.isPending}
                              onClick={() => change(a, x.to)}
                            >
                              {x.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={archiving !== null}
        title={archiving ? `Archive "${archiving.headline}"?` : ''}
        body="It comes off Home right away and moves to the bottom of the list. Its numbers stay."
        confirmLabel="Archive"
        busy={status.isPending}
        onConfirm={() => {
          if (archiving) status.mutate({ ad: archiving, to: 'archived' });
          setArchiving(null);
        }}
        onClose={() => setArchiving(null)}
      />
    </>
  );
}
